/**
 * **Ce qu'un nœud rapporte, en toutes lettres** — SPEC-UI §3.5 : « tooltip de nœud : effet
 * principal · assist d'Atelier (icône du mini-jeu assisté + effet chiffré) · contre-mesures
 * d'événements couvertes · coût · prérequis », et ⟦A-39 §6⟧ : « l'écran de l'arbre dit ce qu'un
 * nœud rapporte ».
 *
 * ## La règle de ce fichier : on LIT le contenu, on ne re-dérive aucune règle
 *
 * Chaque ligne rendue ici est une donnée de `content/skills.json` ou de `content/events.json`,
 * mise en mots — jamais un calcul. Trois conséquences voulues :
 *
 * 1. **Les chiffres sont ceux du contenu, tels quels.** `+0,05 de Qualité` est la `value` de
 *    l'effet ; la borne du moteur (Qualité ≤ 2,0) n'est pas recalculée ici, elle est dite.
 *    Régler les DEGRÉS des assists est de l'équilibrage ⟦A-51⟧ — ce module les affiche, il ne
 *    les juge pas.
 * 2. **La contre-mesure est une relation du contenu** (`event.contremesure.skill_id`), et la
 *    lecture ne fait que retourner l'index : « quels événements citent ce nœud ? ». Le
 *    multiplicateur affiché est `poids_mult`, exactement ce que `events.ts` applique au tirage
 *    (`CONTREMESURE_MULT_DEFAUT` à défaut — importé du moteur, jamais recopié).
 * 3. **Un type d'effet inconnu ne casse rien** : il tombe en texte brut. Le contenu est validé
 *    par Zod au boot, ce cas n'existe donc pas aujourd'hui — mais un 22ᵉ type ne doit pas
 *    rendre l'arbre muet.
 *
 * ## ⟦C62 · lot arbre · A-179⟧ Des mots de joueur, jamais un identifiant
 *
 * Matt, 21/10/2026 : l'Arbre lisait « +0,05 de potentiel Qualité », « désabonnements ×0,90 », « débloque
 * « reporter_evenement » » (un identifiant interne), « +20 ₵ à chaque longue publiée » avant que les Clicks
 * existent. **Les chiffres restent ceux du contenu** (rien n'est recalculé, aucun coût ni effet ne bouge) ; ce
 * sont les MOTS qui changent : un pourcentage au lieu d'un multiplicateur, « de qualité » au lieu de « de
 * potentiel Qualité », « Clicks » au lieu du symbole, et aucun identifiant ne s'écrit jamais à l'écran (un
 * test lit les 42 nœuds et le refuse). Une capacité que le moteur ne lit nulle part (`live` : aucun Live au
 * menu) ne s'annonce plus, comme les systèmes coupés (`CAPACITES_SANS_OBJET`).
 */

import {
  CONTREMESURE_MULT_DEFAUT,
  type AssistParam,
  type Effect,
  type GameEvent,
  type MinigameId,
  type SystemeId,
} from "@maxime/shared";

import { decrireAssist } from "../../atelier/core/libelles";
import { titreCourt } from "../../game/evenementCourt";

/** Un facteur de vidéo, en mots, après « +0,05 » : « +0,05 de qualité ». Ce sont les mots des « rouages » du résultat. */
const FACTEURS: Record<string, string> = {
  accroche: "d'accroche",
  qualite: "de qualité",
  diffusion: "de diffusion",
  portee: "de portée",
};

/** Ce qu'un multiplicateur touche, après « +10 % » : « +10 % d'XP gagnée », « +50 % sur tous les revenus ». */
const CIBLES_MULT: Record<string, string> = {
  xp: "d'XP gagnée",
  rente: "de rente sur les vidéos intemporelles",
  rpm: "de revenus pub",
  vues: "de vues",
  conversion: "d'abonnés gagnés",
  revenus: "sur tous les revenus",
  accroche: "d'accroche",
  qualite: "de qualité",
  diffusion: "de diffusion",
};

/** Ce qu'une capacité permet, une phrase de joueur. Jamais l'identifiant de la capacité. */
const CAPACITES: Record<string, string> = {
  production_evergreen: "Tu peux viser l'intemporel : une vidéo longue qui paie longtemps",
  menage_bibliotheque: "Tu peux faire le ménage : passer une nulle en privé",
  series_bibliotheque: "Tu peux ranger en série (+20 % de rente croisée)",
  delegation_montage: "Tu peux confier le montage à un freelance",
  delegation_totale: "Tu peux confier les cinq étapes à un freelance",
  live: "Tu peux faire des lives",
  potentiel_evergreen_visible: "Tu vois le potentiel intemporel des sujets",
  phase_tendance_visible: "Tu vois la phase et la durée restante des tendances",
  production_deleguee: "Un producteur choisit tes vidéos : tu peux laisser tourner",
  rythme_delegue: "Un manager tient le rythme à ta place",
  guide_metube: "Ouvre le guide MeTube",
  coeur_sur_vous: "Tes réponses cœur aux commentaires valent +50 %",
  community_manager: "Un community manager répond cœur aux commentaires de routine, la nuit",
  reporter_evenement: "Tu peux repousser un événement au lieu de trancher : « je gère »",
};

/** Une capacité que le jeu ne sait pas dire : on le dit sans en écrire l'identifiant. */
const CAPACITE_INCONNUE = "Une capacité de plus";

/**
 * ⟦C62 · lot arbre⟧ Les capacités que le moteur ne lit NULLE PART : `live` est dans le contenu et dans l'enum,
 * mais aucune action Live n'existe au menu (audit du 21/10/2026 : « aucun Live au menu »). L'Arbre ne promet
 * pas ce que le jeu ne tient pas : la ligne n'est pas écrite (la capacité reste dans le contenu, rien n'est
 * supprimé). Le jour où l'action existe, la retirer d'ici suffit.
 */
const CAPACITES_SANS_ECRAN: readonly string[] = ["live"];

/**
 * ⟦C60 · lot b · A-177⟧ Les capacités qui n'ont plus d'objet quand un système est COUPÉ : l'Arbre ne les
 * annonce plus (la capacité reste écrite dans le contenu, elle ne débloque plus rien d'atteignable).
 * Aujourd'hui, la gestion de la bibliothèque : le ménage (`menage`) et les séries (`ranger_serie`) sont
 * refusés par le moteur en `mecanique_verrouillee`.
 */
const CAPACITES_SANS_OBJET: Partial<Record<SystemeId, readonly string[]>> = {
  bibliotheque: ["menage_bibliotheque", "series_bibliotheque"],
};

/** Le sujet d'un coût d'action : « Une vidéo longue coûte 10 % d'énergie en moins ». */
const ACTIONS: Record<string, string> = {
  short: "Un short",
  longue: "Une vidéo longue",
  veille: "La veille",
  lire_commentaires: "Lire les commentaires",
  menage: "Le ménage",
  remaster: "Le remaster",
  dormir: "La nuit",
};

/** Ce qu'un coût consomme, après « coûte 10 % » : « d'énergie ». */
const RESSOURCES: Record<string, string> = {
  heures: "de temps",
  energie: "d'énergie",
  sante_mentale: "de santé mentale",
  argent: "de Clicks",
};

const JAUGES: Record<string, string> = {
  energie: "⚡ énergie",
  sante_mentale: "❤ santé mentale",
  reputation: "⭐ réputation",
  fidelite: "🤝 fidélité",
  argent: "Clicks",
  momentum: "🚀 momentum",
  inspiration: "🃏 idées",
  hype: "💬 hype",
};

/** Une étape de production, après « ta direction » : « Un freelance suit ta direction au tournage ». */
const ETAPES_DIRECTION: Record<string, string> = {
  ecriture: "à l'écriture",
  tournage: "au tournage",
  montage: "au montage",
  miniature: "à la miniature",
  promotion: "à la promotion",
  toutes: "sur les cinq étapes",
};

/** `1,4` → « ×1,40 », `0.6` → « ×0,60 » — un multiplicateur, deux décimales, virgule. */
function fois(v: number): string {
  return `×${v.toFixed(2).replace(".", ",")}`;
}

/** `0.05` → « +0,05 », `-3` → « −3 ». */
function signe(v: number, decimales = 2): string {
  const abs = Math.abs(v);
  const texte = Number.isInteger(abs) ? String(abs) : abs.toFixed(decimales).replace(".", ",");
  return `${v >= 0 ? "+" : "−"}${texte}`;
}

/** Un multiplicateur en pourcentage signé : `1,1` → « +10 % », `0,9` → « −10 % ». */
function pourcent(v: number): string {
  const p = Math.round((v - 1) * 100);
  return `${p >= 0 ? "+" : "−"}${Math.abs(p)} %`;
}

/**
 * Un multiplicateur de TIRAGE en fréquence : `0,5` → « deux fois moins souvent », `0,6` → « 40 % moins
 * souvent », `1,5` → « 50 % plus souvent ». `1` ne change rien : `null`.
 */
export function frequenceLisible(v: number): string | null {
  if (v === 1) return null;
  if (v < 1) {
    const p = Math.round((1 - v) * 100);
    return p === 50 ? "deux fois moins souvent" : `${p} % moins souvent`;
  }
  return `${Math.round((v - 1) * 100)} % plus souvent`;
}

/**
 * Un effet de contenu, en une phrase de joueur. Les **assists** ne passent pas ici : ils ont leur
 * propre bloc (icône du mini-jeu + chiffre), cf. `lectureDuNoeud`.
 */
export function decrireEffet(effet: Effect): string {
  switch (effet.type) {
    case "potential_add":
      return `${signe(effet.value)} ${FACTEURS[effet.factor] ?? effet.factor}`;
    case "multiplier":
      return (
        `${pourcent(effet.value)} ${CIBLES_MULT[effet.target] ?? effet.target}` +
        (effet.duree_h ? ` pendant ${effet.duree_h} h` : "")
      );
    case "unlock":
      return CAPACITES[effet.capability] ?? CAPACITE_INCONNUE;
    case "assist":
      return decrireAssist(effet).texte;
    case "event_weight_mult_event":
      return `Un événement tombe ${frequenceLisible(effet.value) ?? "aussi souvent"}`;
    case "event_weight_mult_categorie":
      return `Une catégorie d'événements tombe ${frequenceLisible(effet.value) ?? "aussi souvent"}`;
    case "action_cost_mult": {
      const sujet = ACTIONS[effet.action] ?? "Cette action";
      const ressource = RESSOURCES[effet.ressource] ?? "de ressource";
      const duree = effet.duree_h ? ` pendant ${effet.duree_h} h` : "";
      if (effet.value === 0.5) return `${sujet} coûte moitié moins ${ressource}${duree}`;
      const p = Math.round(Math.abs(1 - effet.value) * 100);
      return `${sujet} coûte ${p} % ${ressource} en ${effet.value < 1 ? "moins" : "plus"}${duree}`;
    }
    case "money_per_video":
      return `${signe(effet.value, 0)} Clicks à chaque ${
        effet.format === "toutes" ? "vidéo publiée" : effet.format === "short" ? "short publié" : "vidéo longue publiée"
      }`;
    case "money_per_1k_subs_weekly":
      return `${signe(effet.value, 0)} Clicks par semaine et par millier d'abonnés`;
    case "sleep_recovery_add":
      return `${signe(effet.value, 0)} ${JAUGES[effet.gauge] ?? effet.gauge} en plus par nuit`;
    case "freelance_direction":
      return `Un freelance suit ta direction ${ETAPES_DIRECTION[effet.etape] ?? effet.etape} : ${Math.round(effet.value * 100)} % du chemin vers ce que ta chaîne sait faire`;
    case "unsub_mult": {
      const p = Math.round(Math.abs(1 - effet.value) * 100);
      return `${p} % de désabonnements en ${effet.value < 1 ? "moins" : "plus"}`;
    }
    case "strike_delta":
      return `${signe(effet.value, 0)} strike`;
    case "pc_add":
      return `${signe(effet.value, 0)} point${Math.abs(effet.value) >= 2 ? "s" : ""} tout de suite`;
    case "subs_pct":
      return `${signe(effet.value, 0)} % d'abonnés`;
    case "subs_flat":
      return `${signe(effet.value, 0)} abonnés`;
    case "gauge_delta":
      return `${signe(effet.value, 1)} ${JAUGES[effet.gauge] ?? effet.gauge}`;
    case "flag_add":
    case "flag_remove":
      // Un drapeau est un identifiant interne : on dit qu'il y a un effet, jamais son nom.
      return "Un effet particulier";
    case "event_schedule":
      return `Un événement est programmé dans ${effet.delai_h} h`;
    case "image_offset":
      return `${signe(effet.value, 0)} sur l'image de chaîne`;
    default:
      return decrireEffetInconnu(effet);
  }
}

/**
 * Un type d'effet que cette lecture ne connaît pas encore (une session voisine en ajoute un
 * 22ᵉ au moment où ces lignes s'écrivent) : on le dit en clair plutôt qu'en JSON — le type en
 * mots, puis ses champs. L'arbre ne se tait pas, il lit ce qu'il a.
 */
export function decrireEffetInconnu(effet: object): string {
  const { type, ...reste } = effet as { type?: string } & Record<string, unknown>;
  const champs = Object.entries(reste)
    .map(([k, v]) => `${k} ${typeof v === "number" ? signe(v) : String(v)}`)
    .join(", ");
  return `${String(type ?? "effet").replace(/_/g, " ")}${champs ? ` : ${champs}` : ""}`;
}

export interface ContremesureLisible {
  eventId: string;
  nom: string;
  /** Le multiplicateur de tirage que le nœud applique — `poids_mult` du contenu. */
  poidsMult: number;
  /** Le choix qu'il débloque dans l'événement, s'il y en a un. */
  choixDebloque: string | null;
}

export interface LectureNoeud {
  /** Les effets hors assists, une phrase chacun — l'« effet principal » de §3.5. */
  effets: string[];
  /** L'assist d'Atelier : icône et nom du mini-jeu assisté, effet chiffré. */
  assists: { icone: string; jeu: string; texte: string }[];
  /** Les événements dont ce nœud est la contre-mesure (`contremesure.skill_id`). */
  contremesures: ContremesureLisible[];
}

/** Un nœud tel que l'arbre le lit — les champs de contenu dont cette lecture a besoin. */
export interface NoeudLisible {
  id: string;
  effets: readonly Effect[];
  assists?: readonly { minigame: MinigameId; param: AssistParam; value: number }[];
}

/**
 * La lecture complète d'un nœud. **Pure** : le contenu entre, des phrases sortent. Les
 * événements sont passés en paramètre (pas lus d'un singleton) pour rester testable sans boot.
 */
export function lectureDuNoeud(
  noeud: NoeudLisible,
  evenements: readonly Pick<GameEvent, "id" | "nom" | "titre_court" | "contremesure" | "choix">[],
  /** ⟦C60 · lot b⟧ Les systèmes que le ménage a coupés (`systemesCoupes`). Absent ⇒ aucun ⇒ le jeu d'avant. */
  systemesCoupes: readonly SystemeId[] = [],
): LectureNoeud {
  const sansObjet = new Set<string>(systemesCoupes.flatMap((s) => CAPACITES_SANS_OBJET[s] ?? []));
  // ⟦C60 bis · A-177⟧ La réputation a quitté l'écran (arbitrage n° 2, 03/10/2026) : un effet qui ne ferait que la
  // faire bouger ne se lit plus. Il reste dans le contenu et dans le moteur ; rallumée, la lecture le redit.
  const sansReputation = systemesCoupes.includes("reputation");
  const effets = noeud.effets
    .filter((e) => e.type !== "assist")
    .filter((e) => !(e.type === "unlock" && sansObjet.has(e.capability)))
    .filter((e) => !(e.type === "unlock" && CAPACITES_SANS_ECRAN.includes(e.capability)))
    .filter(
      (e) => !(sansReputation && (e.type === "gauge_delta" || e.type === "sleep_recovery_add") && e.gauge === "reputation"),
    )
    .map(decrireEffet);
  const assists = [
    ...noeud.effets.filter((e): e is Extract<Effect, { type: "assist" }> => e.type === "assist"),
    ...(noeud.assists ?? []),
  ].map(decrireAssist);

  const contremesures: ContremesureLisible[] = evenements
    .filter((e) => e.contremesure?.skill_id === noeud.id)
    .map((e) => ({
      eventId: e.id,
      nom: titreCourt(e),
      poidsMult: e.contremesure?.poids_mult ?? CONTREMESURE_MULT_DEFAUT,
      choixDebloque:
        (e.contremesure?.choix_debloque &&
          e.choix?.find((c) => c.id === e.contremesure?.choix_debloque)?.label) ||
        null,
    }));

  return { effets, assists, contremesures };
}

/**
 * ⟦C62 · lot arbre⟧ Une parade, en une phrase de joueur : « Ça grésille » tombe deux fois moins souvent, et le
 * choix qu'elle ouvre. `null` quand elle ne change ni le tirage ni les choix (rien à dire, donc rien d'écrit).
 */
export function texteContremesure(cm: ContremesureLisible): string | null {
  const frequence = frequenceLisible(cm.poidsMult);
  const choix = cm.choixDebloque ? `un choix de plus : « ${cm.choixDebloque} »` : null;
  if (!frequence && !choix) return null;
  return `« ${cm.nom} »${frequence ? ` tombe ${frequence}` : ""}${frequence && choix ? " · " : frequence ? "" : " : "}${choix ?? ""}`;
}

/**
 * Les parades à écrire. Celles qui ouvrent un choix gardent leur ligne (le choix se lit avec son événement) ;
 * celles qui ne changent que le tirage se regroupent par fréquence (« « A », « B » tombent deux fois moins
 * souvent »), sinon *Gestion de crise* écrirait onze lignes qui disent la même chose. Dédoublonnées (deux
 * événements au même titre), sans les muettes.
 */
export function lignesDeContremesures(lecture: LectureNoeud): string[] {
  const avecChoix = new Set<string>();
  const parFrequence = new Map<string, Set<string>>();
  for (const cm of lecture.contremesures) {
    const texte = texteContremesure(cm);
    if (!texte) continue;
    if (cm.choixDebloque) {
      avecChoix.add(texte);
      continue;
    }
    const frequence = frequenceLisible(cm.poidsMult);
    if (!frequence) continue;
    const noms = parFrequence.get(frequence) ?? new Set<string>();
    noms.add(cm.nom);
    parFrequence.set(frequence, noms);
  }
  const groupes = [...parFrequence].map(
    ([frequence, noms]) =>
      `${[...noms].map((n) => `« ${n} »`).join(", ")} ${noms.size > 1 ? "tombent" : "tombe"} ${frequence}`,
  );
  return [...avecChoix, ...groupes];
}

/** La lecture, aplatie en lignes — pour un `title` ou le corps d'un dialogue. */
export function lignesDeLecture(lecture: LectureNoeud): string[] {
  const lignes = [...lecture.effets];
  for (const a of lecture.assists) lignes.push(`${a.icone} ${a.jeu} : ${a.texte}`);
  for (const t of lignesDeContremesures(lecture)) lignes.push(`🛡 ${t}`);
  return lignes;
}

export { fois as multiplicateurLisible };
